import { useEffect, useRef } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useToaster } from './Toaster';
import { selectUser } from '../redux/slices/userSlice';
import {
  fetchTeams,
  selectTeams,
  setTeamSelected,
  clearTeamsError,
  setSettingForm,
} from '../redux/slices/teamsSlice';
import styles from './Teams.module.css';
import { useNavigate } from 'react-router-dom';

export default function Teams() {
  const user = useSelector(selectUser);
  const account_token = user?.data?.token;
  const teams = useSelector(selectTeams);
  const teamsOption = (teams?.teams ?? []).map((item) => ({ value: item.token, label: item.name }));
  const dispatch = useDispatch();
  const { push } = useToaster();
  const navigate = useNavigate();
  const hasMounted = useRef(false);

  const onTeamChange = (opt) => {
    const nextTeam = opt?.value || '';
    dispatch(setTeamSelected(nextTeam));
    dispatch(setSettingForm({ name: teams?.teams?.find((item) => item.token === nextTeam)?.name || '' }));
  };

  useEffect(() => {
    const e = teams.error;
    const msg = (e && (e.msg || e.message)) || '';

    if (msg) {
      push({ title: '發生錯誤', message: msg, variant: 'danger' });
      dispatch(clearTeamsError());
    }
  }, [teams.error, push, dispatch]);

  useEffect(() => {
    if (account_token) {
      dispatch(fetchTeams({ account_token }));
    }
  }, [account_token, dispatch]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (teams?.no_teams == true) {
      navigate('/team-create', { replace: true });
    }
  }, [teams.no_teams, navigate]);

  return (
    <section className={styles.teamsWrap} aria-label="Teams 面板">
      <div className={styles.panel}>
        <div className={`${styles.content} row g-3 align-items-center`}>
          <div className="col-12 col-md-6 d-flex align-items-center">
            <label
              htmlFor="accountTeams"
              className={`${styles.label} form-label mb-0 me-2`}
            >
              <span className={styles.labelDot} aria-hidden="true" />
              團隊
            </label>

            <div className={styles.selectWrap}>
              <Select
                inputId="accountTeams"
                instanceId="accountTeams"
                className={styles.select}
                options={teamsOption}
                value={teamsOption.find((o) => o.value === teams.team_selected) || null}
                onChange={onTeamChange}
                placeholder="選擇一個團隊…"
                isSearchable
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 2200 }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: '#ffffff',
                    borderColor: state.isFocused ? '#d84040' : '#d0d3d8',
                    boxShadow: state.isFocused
                      ? 'inset 0 1px 2px rgba(0,0,0,.10), 0 0 0 2px #fff, 0 0 0 4px rgba(255,64,64,.5)'
                      : 'inset 0 1px 2px rgba(0,0,0,.08)',
                    borderRadius: 8,
                    minHeight: 40,
                    outline: 'none',
                    transition: 'box-shadow .2s ease, border-color .2s ease',
                    ':hover': {
                      borderColor: state.isFocused ? '#d84040' : 'rgba(0,0,0,.2)',
                    },
                  }),
                  singleValue: (base) => ({ ...base, color: '#101014' }),
                  placeholder: (base) => ({ ...base, color: '#868e96' }),
                  dropdownIndicator: (base, state) => ({
                    ...base,
                    color: state.isFocused ? '#d84040' : '#495057',
                    ':hover': { color: '#d84040' },
                  }),
                  indicatorSeparator: (base) => ({ ...base, backgroundColor: 'rgba(0,0,0,.12)' }),
                  menu: (base) => ({
                    ...base,
                    overflow: 'hidden',
                    marginTop: 6,
                    borderRadius: 8,
                    backgroundColor: '#ffffff',
                    border: '1px solid #d84040',
                    boxShadow: '0 18px 36px rgba(0,0,0,.35), 0 0 0 1px rgba(0,0,0,.1)',
                    zIndex: 2200,
                  }),
                  menuList: (base) => ({
                    ...base,
                    padding: 4,
                    backgroundColor: '#ffffff',
                  }),
                  option: (base, state) => ({
                    ...base,
                    padding: '10px 14px',
                    borderRadius: 6,
                    transition: 'background .15s ease, color .15s ease, box-shadow .15s ease',
                    fontWeight: state.isSelected ? 700 : 500,
                    color: state.isDisabled ? '#ccc' : '#101014',
                    backgroundColor: state.isSelected || state.isFocused ? 'rgba(216,64,64,.1)' : 'transparent',
                    boxShadow: state.isSelected || state.isFocused ? 'inset 4px 0 0 0 #d84040' : 'none',
                    cursor: state.isDisabled ? 'not-allowed' : 'default',
                    ':active': {
                      backgroundColor: state.isDisabled ? 'transparent' : 'rgba(216,64,64,.14)',
                    },
                  }),
                }}
              />
            </div>
          </div>

          <div className="col-12 col-md-6 d-flex justify-content-md">
            <button
              className={styles.btnCreate}
              type="button"
              onClick={() => { navigate('/team-create', { replace: true }); }}
            >
              <i className="bi bi-plus-circle" />
              建立新團隊
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
